import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('fundfair_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('fundfair_token');
      // Only redirect for expired/invalid sessions of logged-in users.
      // Anonymous browsers receive 401 from endpoints they aren't allowed to call —
      // surface those errors to the caller instead of yanking them to /login.
      if (token) {
        localStorage.removeItem('fundfair_token');
        const path = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
        if (typeof window !== 'undefined' && !path.startsWith('/login')) {
          window.location.href = `/login?next=${encodeURIComponent(path)}`;
        }
      }
    }
    return Promise.reject(error);
  }
);

// Format MYR from cents
export const formatMYR = (cents) => {
  return `MYR ${(cents / 100).toFixed(2)}`;
};

// Parse MYR to cents
export const parseMYRToCents = (myrString) => {
  const value = parseFloat(myrString);
  if (isNaN(value)) return 0;
  return Math.round(value * 100);
};

// Format date
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-MY', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Format datetime
export const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-MY', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Calculate progress percentage
export const calculateProgress = (raised, target) => {
  if (target <= 0) return 0;
  return Math.min(100, Math.round((raised / target) * 100));
};

// Check if campaign is active
export const isCampaignActive = (campaign) => {
  if (campaign.status !== 'APPROVED') return false;
  if (campaign.ended_at) return false;
  const deadline = new Date(campaign.deadline_date);
  return deadline > new Date();
};

// Days remaining
export const daysRemaining = (deadline) => {
  const now = new Date();
  const end = new Date(deadline);
  const diff = end - now;
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return Math.max(0, days);
};

// Open file URL - handles both regular URLs and base64 data URLs
export const openFileUrl = (url, filename = 'file') => {
  if (!url) return;
  
  // Check if it's a base64 data URL
  if (url.startsWith('data:')) {
    // Create a blob from the data URL and open it
    const [header, base64Data] = url.split(',');
    const mimeMatch = header.match(/data:([^;]+)/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      console.error('Error opening file:', e);
      // Fallback: try to download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
    }
  } else {
    // Regular URL - open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};
